import { sha256 } from '../port/k2-geometry/sha256';
import { json_encode } from '../php-compat';
import { readItemField } from './item-field-reader';
import { fromBaseClone, fromBaseRecord, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { roundPhpDecimal } from './php-decimal-values';

export const MAGIC_CATALOG_ITEM_ID = '0C25137F-B688-8D55-50E9-9B580781FD5D';
export interface FromBaseMagicCatalogInputs { pilot: Row | null; textures: Row | null; audioRows: readonly Row[] }

/** Native pilot catalog construction from explicit asset/SELECT inputs. */
export function constructFromBaseMagicCatalog(recipe: Row, inputs?: FromBaseMagicCatalogInputs): Row | null {
    if (readItemField(recipe, 'kind') !== 'projectile') return null;
    if (!inputs) throw new FromBaseStageMissing('definition-magic-catalog-inputs', MAGIC_CATALOG_ITEM_ID);
    const takes = readItemField({ takes: [inputs.pilot?.impact ?? null] }, 'takes');
    if (!Array.isArray(takes) || takes.length < 1 || takes.length > 16) return null;
    const rows = new Map(inputs.audioRows.map(row => [phpJsonString(readItemField(row, 'audio_id')).toUpperCase(), row]));
    const impact: Row[] = [];
    for (const take of takes) {
        if (!fromBaseRecord(take)) return null;
        const row = rows.get(phpJsonString(readItemField(take, 'audioId')));
        const hash = readItemField(take, 'sha256');
        if (!row || phpJsonInt(readItemField(row, 'audio_private')) !== 0 || phpJsonInt(readItemField(row, 'audio_deleted')) !== 0
            || readItemField(row, 'audio_type') !== 'sfx' || readItemField(row, 'audio_category') !== 'damage'
            || readItemField(row, 'audio_subcategory') !== 'force' || phpJsonTruthy(readItemField(row, 'audio_linked_item_id'))
            || readItemField(row, 'audio_format') !== readItemField(take, 'format')
            || typeof hash !== 'string' || !/^[a-f0-9]{64}$/.test(hash) || readItemField(take, 'revision') !== hash) return null;
        impact.push(Object.fromEntries(Object.entries(take).filter(([key]) => ['audioId', 'revision', 'url', 'format', 'sha256'].includes(key))));
    }
    const textures = fromBaseRecord(inputs.textures?.assets) ? inputs.textures.assets : {};
    const texture = textures[phpJsonString(readItemField(recipe, 'texture'))];
    if (!fromBaseRecord(texture)) return null;
    const duration = roundPhpDecimal(Number(readItemField(recipe, 'duration')) * 1000, 0);
    const variant: Row = { id: 'violet-impact-v1', recipe: fromBaseClone(recipe), textureRevision: readItemField(texture, 'sha256'),
        impactOffsetMs: roundPhpDecimal(duration * 0.52, 0), durationMs: duration };
    const hash = (row: Row): string => { const encoded = json_encode(row); if (encoded === false) throw new TypeError('Non-JSON magic catalog'); return sha256(encoded); };
    variant.revision = hash(variant);
    const catalog: Row = { schema: 'ft.magic-catalog.v1', itemId: MAGIC_CATALOG_ITEM_ID, variants: [variant], impact };
    catalog.catalogRevision = hash(catalog);
    return catalog;
}
